import Box from "@mui/material/Box/Box";
import Chip from "@mui/material/Chip/Chip";
import FormControl from "@mui/material/FormControl/FormControl";
import InputLabel from "@mui/material/InputLabel/InputLabel";
import MenuItem from "@mui/material/MenuItem/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput/OutlinedInput";
import Select, { SelectChangeEvent } from "@mui/material/Select/Select";
import { Theme, useTheme } from "@mui/material/styles";
import Rails from "@rails/ujs";
import Axios from "axios";
import * as i18n from "i18n-js";
import * as React from "react";
import { Button, Modal } from "react-bootstrap";
import toastr from "toastr";
import { PackingItemData, ShippingMark } from "../Common/types";

//@ts-ignore
i18n.translations = I18n.translations || {};

interface EditPackingItemProps {
  item: PackingItemData;
  handleUpdatedPackingItem: () => void;
  disabled?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  style: { zIndex: 2400 },
};

function getStyles(
  markId: number,
  shippingMark: readonly number[],
  theme: Theme
) {
  return {
    fontWeight: shippingMark.some((m) => m === markId)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

const EditPackingItem = (props: EditPackingItemProps) => {
  const [show, setShow] = React.useState(false);

  const [width, setWidth] = React.useState(props.item.width || "100");
  const [widthError, setWidthError] = React.useState("");

  const [height, setHeight] = React.useState(props.item.height || "120");
  const [heightError, setHeightError] = React.useState("");

  const [depth, setDepth] = React.useState(props.item.depth || "120");
  const [depthError, setDepthError] = React.useState("");

  const [packName, setPackName] = React.useState(props.item.pack_name || "");

  const theme = useTheme();
  const [shippingMark, setShippingMark] = React.useState<number[]>(
    props.item.shipping_marks.map((sm) => sm.id) || []
  );
  const [shippingMarks, setShippingMarks] = React.useState<ShippingMark[]>([]);
  console.log("shippingMark", shippingMark);
  const handleChange = (event: SelectChangeEvent<number[]>) => {
    const {
      target: { value },
    } = event;
    console.log("handleChange value", value);
    //@ts-ignore
    setShippingMark(value);
  };

  const handleClose = () => setShow(false);

  function buttonClicked() {
    setShow(true);
  }

  const link = (
    <button
      onClick={buttonClicked}
      className={`btn btn-info btn-sm`}
      data-toggle="tooltip"
      data-placement="top"
      title="EDIT"
      disabled={props.disabled}
    >
      <i className="fa fa-pencil" />
    </button>
  );

  function isNumber(n) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  function loadShippingMarks() {
    Axios({
      method: "GET",
      url: `/shipping_marks.json`,
      headers: {
        "X-CSRF-Token": Rails.csrfToken(),
        "Content-Type": "application/json",
      },
    }).then((res) => {
      setShippingMarks(res.data);
    });
  }

  function reloadTasksTable() {
    Axios({
      method: "GET",
      url: `/projects/${props.item.project_id}/tasks`,
      headers: {
        "X-CSRF-Token": Rails.csrfToken(),
        "Content-Type": "application/json",
      },
    }).then((res) => {
      //@ts-ignore
      $("#packaging-tasks-table").html(res.data);
    });
  }

  function submitForm(): void {
    Axios({
      method: "PATCH",
      url: `/projects/${props.item.project_id}/packing_items/${props.item.id}.json`,
      data: {
        packing_item: { width: width, height: height, depth: depth, pack_name: packName },
        shipping_marks: shippingMark,
      },
      headers: {
        "X-CSRF-Token": Rails.csrfToken(),
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        setShow(false);
        toastr.success("Packing item is updated");
        reloadTasksTable();
        props.handleUpdatedPackingItem();
      })
      .catch((err) => {
        console.log(err);
        toastr.error("Packing item is NOT updated", err);
      });
  }

  function widthChanged(newVal: string): void {
    setWidth(newVal);
    setWidthError(validateNumber(newVal, 5, 1250));
  }

  function heightChanged(newVal: string): void {
    setHeight(newVal);
    setHeightError(validateNumber(newVal, 5, 250));
  }

  function depthChanged(newVal: string): void {
    setDepth(newVal);
    setDepthError(validateNumber(newVal, 5, 300));
  }


  function validateNumber(
    newVal: string,
    minimum: number,
    maximum: number
  ): string {
    if (!isNumber(newVal)) {
      return "It is not numeric";
    } else {
      let n = parseFloat(newVal);
      if (n > maximum) {
        return `Too big - maximum allowed is ${maximum}`;
      } else if (n < minimum) {
        return `Too small - minimum allowed is ${minimum}`;
      }
    }

    return "";
  }

  const isValid = widthError == "" && heightError == "" && depthError == "";

  React.useEffect(() => {
    if (show) {
      loadShippingMarks();
    }
  }, [show]);

  console.log("show", show);

  return (
    <>
      {link}
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Packing Item</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className="form-group row">
              <label className="col-lg-6 col-form-label">Length</label>
              <div className="col-lg-6">
                <div className="input-group m-b">
                  <input
                    type="text"
                    placeholder="length"
                    value={width}
                    onChange={(e) => widthChanged(e.target.value)}
                    className="form-control"
                  />

                  <div className="input-group-prepend">
                    <span className="input-group-addon">cm</span>
                  </div>
                </div>
                <span className="form-text text-danger m-b-none">
                  {widthError}
                </span>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-lg-6 col-form-label">Height</label>
              <div className="col-lg-6">
                <div className="input-group m-b">
                  <input
                    type="text"
                    placeholder="height"
                    value={height}
                    onChange={(e) => heightChanged(e.target.value)}
                    className="form-control"
                  />
                  <div className="input-group-prepend">
                    <span className="input-group-addon">cm</span>
                  </div>
                </div>

                <span className="form-text text-danger m-b-none">
                  {heightError}
                </span>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-lg-6 col-form-label">Depth</label>
              <div className="col-lg-6">
                <div className="input-group m-b">
                  <input
                    type="text"
                    placeholder="depth"
                    value={depth}
                    onChange={(e) => depthChanged(e.target.value)}
                    className="form-control"
                  />
                  <div className="input-group-prepend">
                    <span className="input-group-addon">cm</span>
                  </div>
                </div>

                <span className="form-text text-danger m-b-none">
                  {depthError}
                </span>
              </div>
            </div>

            <div className="form-group row">
              <label className="col-lg-4 col-form-label">Shipping Marks</label>
              <div className="col-lg-8">
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel id="shipping-marks-label">
                    Shipping marks
                  </InputLabel>
                  <Select
                    labelId="shipping-marks-label"
                    id="shipping-marks"
                    multiple
                    value={shippingMark}
                    onChange={handleChange}
                    input={
                      <OutlinedInput
                        id="select-multiple-chip"
                        label="Shipping marks"
                      />
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={
                              shippingMarks.find((sm) => sm.id === value)?.name
                            }
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={MenuProps}
                  >
                    {shippingMarks.map((mark) => (
                      //@ts-ignore
                      <MenuItem
                        key={mark.id}
                        value={mark.id}
                        style={getStyles(mark.id, shippingMark, theme)}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                          }}
                        >
                          <span>{mark.name}</span>
                          {!!mark.url && (
                            <img
                              width="18px"
                              height="24px"
                              src={mark.url}
                              alt={mark.name}
                            ></img>
                          )}
                        </div>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>


            <div className="form-group row">
              <label className="col-lg-6 col-form-label">Pack Name</label>
              <div className="col-lg-6">
                <div className="input-group m-b">
                  <input
                    type="text"
                    placeholder=""
                    value={packName}
                    onChange={(e) => setPackName(e.target.value)}
                    className="form-control"
                  />
                </div>
              </div>
            </div>


          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="default" onClick={handleClose}>
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={() => submitForm()}
            disabled={!isValid}
          >
            Update
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default EditPackingItem;
